import { html, nothing } from "lit";
import { repeat } from "lit/directives/repeat.js";
import type { ChatItem, MessageGroup } from "../types/chat-types.ts";
import { renderRichToolClusterGroup } from "../chat/grouped-render.ts";
import { renderRichMessage } from "../chat/rich-message-render.ts";
import type { ChatRenderState, ChatProps } from "./chat.ts";

function renderRichReadingIndicator(props: ChatProps) {
  return html`
    <section class="chat-rich-group chat-rich-group--assistant chat-rich-group--streaming">
      <header class="chat-rich-group__meta">
        <span class="chat-rich-group__sender">${props.assistantName}</span>
      </header>
      <div class="chat-rich-group__messages">
        <article class="chat-rich-message chat-rich-message--assistant chat-rich-message--reading">
          <div class="chat-rich-reading-indicator" aria-hidden="true">
            <span class="chat-reading-indicator__dots">
              <span></span><span></span><span></span>
            </span>
          </div>
        </article>
      </div>
    </section>
  `;
}

function renderRichGroup(
  group: MessageGroup,
  props: ChatProps,
  state: ChatRenderState,
) {
  if (state.deleted.has(group.key)) {
    return nothing;
  }

  const normalizedRole = group.role === "assistant" || group.role === "user" ? group.role : "assistant";
  const who = normalizedRole === "user" ? "你" : props.assistantName;
  const timestamp = new Date(group.timestamp).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  return html`
    <section class="chat-rich-group chat-rich-group--${normalizedRole}">
      <header class="chat-rich-group__meta">
        <span class="chat-rich-group__sender">${who}</span>
        <span class="chat-rich-group__timestamp">${timestamp}</span>
      </header>
      <div class="chat-rich-group__messages">
        ${group.messages.map((item, index) =>
          renderRichMessage(item.message, {
            showReasoning: state.showReasoning,
            showToolCalls: props.showToolCalls,
            isStreaming: group.isStreaming && index === group.messages.length - 1,
            onOpenSidebar: props.onOpenSidebar,
          }),
        )}
      </div>
    </section>
  `;
}

export function renderChatRichTimeline(
  items: Array<ChatItem | MessageGroup>,
  props: ChatProps,
  state: ChatRenderState,
) {
  const handleCodeBlockCopy = (e: Event) => {
    const btn = (e.target as HTMLElement).closest(".code-block-copy");
    if (!btn) {
      return;
    }
    const code = (btn as HTMLElement).dataset.code ?? "";
    navigator.clipboard.writeText(code).then(
      () => {
        btn.classList.add("copied");
        setTimeout(() => btn.classList.remove("copied"), 1500);
      },
      () => {},
    );
  };

  return html`
    <div
      class="chat-thread chat-rich-thread"
      role="log"
      aria-live="polite"
      @scroll=${props.onChatScroll}
      @click=${handleCodeBlockCopy}
    >
      <div class="chat-thread-inner chat-rich-thread__inner">
        ${repeat(items, (item) => item.key, (item) => {
          if (item.kind === "divider") {
            return html`
              <div class="chat-divider chat-rich-divider" role="separator" data-ts=${String(item.timestamp)}>
                <span class="chat-divider__line"></span>
                <span class="chat-divider__label">${item.label}</span>
                <span class="chat-divider__line"></span>
              </div>
            `;
          }
          if (item.kind === "reading-indicator") {
            return renderRichReadingIndicator(props);
          }
          if (item.kind === "stream") {
            return html`
              <section class="chat-rich-group chat-rich-group--assistant chat-rich-group--streaming">
                <header class="chat-rich-group__meta">
                  <span class="chat-rich-group__sender">${props.assistantName}</span>
                  <span class="chat-rich-group__timestamp"
                    >${new Date(item.startedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span
                  >
                </header>
                <div class="chat-rich-group__messages">
                  ${renderRichMessage(
                    {
                      role: "assistant",
                      content: [{ type: "text", text: item.text }],
                      timestamp: item.startedAt,
                    },
                    {
                      showReasoning: false,
                      showToolCalls: props.showToolCalls,
                      isStreaming: true,
                      onOpenSidebar: props.onOpenSidebar,
                    },
                  )}
                </div>
              </section>
            `;
          }
          if (item.kind === "tool-cluster") {
            return renderRichToolClusterGroup(item, {
              onOpenSidebar: props.onOpenSidebar,
              assistantName: props.assistantName,
            });
          }
          if (item.kind === "group") {
            return renderRichGroup(item, props, state);
          }
          return nothing;
        })}
      </div>
    </div>
  `;
}
